import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bookingActions } from "../../store/reducers/booking";
import { uiActions } from "../../store/reducers/ui";

import { DateTime } from "luxon";

import useApi from "../../hooks/useApi";
import constants, { constant_names, FLOW } from "../../utils/constants";
import WidgetDialog from "./WidgetDialog";
import CloseConfirmationDialog from "./CloseConfirmationDialog";

const paramsString = window.location.search;
const urlParams = new URLSearchParams(paramsString);
const requestType = urlParams.get("type") || constants.REQUEST_TYPES.INQUIRY;
const source = urlParams.get("source") || "";

const cleanParam = (param) => {
  return param.replace(/[- )(]/g, "");
};

/** If query parameter is set, converts param from HEX to string. Otherwise, returns empty */
const getParam = (param) => {
  const paramValue = urlParams.get(param);
  let stringParam = "";

  if (paramValue) {
    if (param[0] === "h") {
      const hexParam = paramValue.toString(); // Only execute toString if the param exists

      if (hexParam) {
        for (let i = 0; i < hexParam.length; i += 2) {
          stringParam += String.fromCharCode(parseInt(hexParam.substring(i, i + 2), 16));
        }

        if (param === "phone" || param === "hPhone" || param === "postalCode" || param === "hPostalCode") {
          return cleanParam(stringParam);
        }
      }
    } else {
      return paramValue;
    }
  }

  return stringParam;
};

// Converts the accident date to ISO format if it is available
let accidentDate = getParam("hAccDate") || getParam("accDate") || "";

if (accidentDate) {
  if (source === "ld") {
    accidentDate = accidentDate.replace(" ", "+"); //ISO + is replaced by a space when getting the param
  } else {
    accidentDate = DateTime.fromFormat(accidentDate, "dd/MM/yyyy");

    if (!accidentDate.invalid) {
      accidentDate = accidentDate.toISO();
    }
  }
}

//?type=2&qualified=true&ck=1044995&hFName=446965676f&hMName=546865&hLName=546573746572&hPhone=34303331323334353637&hEmail=64776569646c65406c6974636f2e6361&hStreet=3132332031323334205354205357&hCity=43697479&hProvince=4142&hPostalCode=543252304731&hAccCity=43616c67617279&hAccDate=30362f31322f32303231
//?type=2&qualified=true&source=ld&ck=6&fName=Diego&lName=LD&mName=Tester&phone=4038056260&email=dweidle@litco.ca&city=Calgary&street=12345 40 St SE&postalCode=T2Z%204E6&accCity=Calgary&province=AB&accDate=2023-07-05T06:00:00.0000000+00:00

export default function Widget() {
  const cancelDialogOpen = useSelector((state) => state.ui.cancelDialogOpen);

  const dispatch = useDispatch();

  const { isLoading: loadingBranchesInfo, sendRequest: getBranchInfo } = useApi({
    initialLoading: true,
    successMessage: "Branches config loaded successfully",
    errorMessage: "Error loading branches config",
  });

  const branchesInfoCallback = useCallback(
    (data) => {
      const branchesInfo = data.branchesInfo;
      const processedBranchesInfo = {};
      const requestTypeName = constant_names.REQUEST_TYPE_NAMES[requestType].toLowerCase();

      Object.keys(branchesInfo).forEach((branchCode) => {
        const branchInfo = branchesInfo[branchCode];

        branchInfo.weekStart = constants.WEEKDAYS[branchInfo.weekStart.toUpperCase()];
        branchInfo.weekEnd = constants.WEEKDAYS[branchInfo.weekEnd.toUpperCase()];
        branchInfo.slotsInterval = +branchInfo[`${requestTypeName}Interval`];
        branchInfo.slotsAllocation = +branchInfo[`${requestTypeName}Allocation`];
        branchInfo.minLeadTime = +branchInfo[`${requestTypeName}MinLeadTime`];
        branchInfo.maxLeadTime = +branchInfo[`${requestTypeName}MaxLeadTime`];
        branchInfo.concurrentMeetings = +branchInfo.concurrentMeetings;
        branchInfo.apptOnly = +branchInfo.apptOnly;

        processedBranchesInfo[branchCode] = branchInfo;
      });

      dispatch(bookingActions.updateBranchesInfo(processedBranchesInfo));
    },
    [dispatch]
  );

  const populateInitialClientDetails = useCallback(() => {
    const analytics = {
      source: getParam("utm_source"),
      medium: getParam("utm_campaign"),
      campaign: getParam("utm_medium"),
    };

    if (requestType === constants.REQUEST_TYPES.INQUIRY) {
      const initialClientDetails = {
        meetingType: constants.MEETING_TYPES.PHONE,
        selectedBranch: "9999", //Remote
        additionalInfo: {
          contactPreference: "noPreference",
          lawArea: "",
          lawAreaDetails: "",
          description: "",
        },
        analytics,
        source,
      };

      dispatch(bookingActions.updateClientDetails(initialClientDetails));

      dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.QUALIFIED));
    } else {
      dispatch(bookingActions.updateRequestType(constants.REQUEST_TYPES.CONSULT));

      const meetingType = +getParam("hMeetingType") || +getParam("meetingType") || constants.MEETING_TYPES.NOT_SELECTED;
      const isRemote = meetingType === constants.MEETING_TYPES.PHONE || meetingType === constants.MEETING_TYPES.VIRTUAL;
      const street = getParam("hStreet") || getParam("street");
      const city = getParam("hCity") || getParam("city");
      const province = getParam("hProvince") || getParam("province");
      const postalCode = getParam("hPostalCode") || getParam("postalCode");

      const initialClientDetails = {
        caseKey: getParam("ck"),
        firstName: getParam("hFName") || getParam("fName"),
        middleName: getParam("hMName") || getParam("mName"),
        lastName: getParam("hLName") || getParam("lName"),
        phone: getParam("hPhone") || getParam("phone"),
        email: getParam("hEmail") || getParam("email"),
        meetingType: meetingType,
        selectedBranch: isRemote ? "9999" : null,
        street,
        city,
        province,
        postalCode,
        accidentCity: getParam("hAccCity") || getParam("accCity"),
        additionalInfo: {
          lawArea: "Personal Injury",
          address: `${street}, ${city}, ${province} ${postalCode}`,
          accidentDate,
          policeFileNumber: "",
          description: "",
          numberOfOccupants: 1,
          driverOrPassenger: "driver",
          driverPassengerOther: "",
          otherOccupants: {},
          willOthersAttend: true,
        },
        analytics,
        source,
      };

      dispatch(bookingActions.updateClientDetails(initialClientDetails));

      if (getParam("qualified")) {
        dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.PRE_QUALIFIED));
      } else {
        dispatch(bookingActions.updateStatus(constants.BOOKING_STATUS.QUALIFYING));
      }

      if (initialClientDetails.selectedBranch) {
        dispatch(uiActions.updateActiveStep(FLOW[constants.REQUEST_TYPES.CONSULT].indexOf(constants.STEPS.BOOKING)));
      } else if (initialClientDetails.meetingType !== constants.MEETING_TYPES.NOT_SELECTED) {
        dispatch(uiActions.updateActiveStep(FLOW[constants.REQUEST_TYPES.CONSULT].indexOf(constants.STEPS.ADDRESS)));
      }
    }
  }, [dispatch]);

  useEffect(() => {
    getBranchInfo({ url: "api/booking-app/branches-info.php" }, branchesInfoCallback);
    populateInitialClientDetails();
  }, [getBranchInfo, branchesInfoCallback, populateInitialClientDetails]);

  return (
    <>
      <WidgetDialog branchInfoLoading={loadingBranchesInfo} requestType={requestType} />

      {cancelDialogOpen && <CloseConfirmationDialog open={cancelDialogOpen} requestType={requestType} />}
    </>
  );
}
